import { useState } from "react";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { MoveRight, Merge, UtensilsCrossed, CheckSquare } from "lucide-react";
import { tableTone, toneLabel } from "@/lib/occupancy";
import * as api from "@/lib/orderApi";
import { toast } from "sonner";

function groupItemsByKot(items) {
  const groups = {};
  items.forEach(item => {
    const key = item.kot_number || "Unsent";
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return groups;
}

export function TableActionModals({ actionTable, actionType, setActionType, selectedItemIds, setSelectedItemIds, token, tables, orderForTable, onSuccess }) {
  const [orderItems, setOrderItems] = useState([]);
  // The selection is the board's, passed in: the board sends it when the captain
  // taps the destination table. A private copy here meant the board always sent
  // an empty list — "Select at least one item to transfer", ten times on
  // 2026-10-13 (C05, order 765).
  const [itemsLoading, setItemsLoading] = useState(false);
  const [working, setWorking] = useState(false);

  const handleActionSelect = async (type) => {
    if (type === 'shift') {
      setActionType('select_target_shift');
    } else if (type === 'items') {
      const running = orderForTable(actionTable.id);
      // For CaptainPAD, we must use server_order_id if it's a mesh order, or id if it's a rest order
      const serverId = running?.server_order_id || running?.id;
      if (!serverId) {
        toast.error("Order is not synced to server yet. Sync first before moving items.");
        return;
      }
      setItemsLoading(true);
      setActionType('select_items');
      try {
        const items = await api.getOrderItems(serverId, token);
        setOrderItems(items);
        setSelectedItemIds(new Set());
      } catch (err) {
        toast.error("Failed to load order items");
        setActionType(null);
      } finally {
        setItemsLoading(false);
      }
    }
  };

  const toggleItemSelection = (id) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleKotSelection = (itemsInKot, isAllSelected) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      itemsInKot.forEach(item => {
        if (isAllSelected) next.delete(item.id);
        else next.add(item.id);
      });
      return next;
    });
  };

  if (actionType === "select_action") {
    return (
      <Modal title={`Manage ${actionTable.name}`} onClose={() => setActionType(null)} testId="action-choice-modal">
        <div className="flex flex-col gap-4 p-5">
          <Button variant="outline" className="h-20 w-full text-body flex justify-start items-center gap-5 px-6 rounded-xl hover:border-primary hover:bg-primary/5 transition-all" onClick={() => handleActionSelect('shift')}>
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Merge className="w-5 h-5 text-primary" />
            </div>
            <div className="text-left flex flex-col justify-center">
              <strong className="block text-base">Move / Merge Table</strong>
              <small className="text-muted-foreground font-normal text-sm mt-0.5">Shift the entire order to another table</small>
            </div>
          </Button>
          <Button variant="outline" className="h-20 w-full text-body flex justify-start items-center gap-5 px-6 rounded-xl hover:border-primary hover:bg-primary/5 transition-all" onClick={() => handleActionSelect('items')}>
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <UtensilsCrossed className="w-5 h-5 text-primary" />
            </div>
            <div className="text-left flex flex-col justify-center">
              <strong className="block text-base">Move KOT / Items</strong>
              <small className="text-muted-foreground font-normal text-sm mt-0.5">Select specific items to transfer</small>
            </div>
          </Button>
        </div>
      </Modal>
    );
  }

  if (actionType === "select_items") {
    const groups = groupItemsByKot(orderItems.filter(i => i.status !== 'cancelled'));
    const allIds = orderItems.filter(i => i.status !== 'cancelled').map(i => i.id);
    const hasItems = allIds.length > 0;
    const canSubmit = selectedItemIds.size > 0 && selectedItemIds.size < allIds.length;

    return (
      <Modal title={`Select items from ${actionTable.name}`} onClose={() => setActionType(null)} testId="select-items-modal">
        <div className="p-4 max-h-[60vh] overflow-y-auto space-y-4">
          {itemsLoading ? (
            <div className="py-8 text-center text-muted-foreground">Loading items...</div>
          ) : !hasItems ? (
            <div className="py-8 text-center text-muted-foreground">No active items on this order.</div>
          ) : (
            Object.entries(groups).map(([kotName, items]) => {
              const isAllSelected = items.every(i => selectedItemIds.has(i.id));
              return (
                <div key={kotName} className="border rounded-xl overflow-hidden">
                  <header 
                    className="flex justify-between items-center bg-muted/40 px-3 py-2 border-b cursor-pointer hover:bg-muted/60"
                    onClick={() => toggleKotSelection(items, isAllSelected)}
                  >
                    <strong className="text-sm font-semibold">{kotName === "Unsent" ? "Unsent Items" : `KOT #${kotName}`}</strong>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${isAllSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-input bg-background'}`}>
                      {isAllSelected && <CheckSquare className="w-4 h-4" />}
                    </div>
                  </header>
                  <div className="divide-y">
                    {items.map(item => {
                      const isSelected = selectedItemIds.has(item.id);
                      return (
                        <div 
                          key={item.id} 
                          className="flex justify-between items-center px-3 py-2 bg-card hover:bg-muted/20 cursor-pointer"
                          onClick={() => toggleItemSelection(item.id)}
                        >
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{item.item_name} {item.variation_name && `(${item.variation_name})`}</span>
                            <span className="text-xs text-muted-foreground">Qty: {item.quantity}</span>
                          </div>
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-input bg-background'}`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="p-4 border-t bg-muted/10 flex justify-between items-center">
          <small className="text-muted-foreground">
            {selectedItemIds.size === allIds.length && allIds.length > 0 
              ? "To move all items, use Move/Merge Table instead." 
              : `${selectedItemIds.size} selected`}
          </small>
          <Button variant="action" 
            disabled={!canSubmit} 
            onClick={() => setActionType("select_target_items")}
          >
            Next <MoveRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </Modal>
    );
  }

  // The actual Target Selection modal was deleted. We rely on CaptainTables.jsx
  // to render a banner or guide when actionType is select_target_shift|select_target_items
  return null;
}
